import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client/react";

import FlashContext from "components/FlashMessages/FlashContext";
import PageWrapper from "components/PageWrapper";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Card from "ds/components/Card";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { UPDATE_SECURITY_EMAIL } from "./gql";

type SecuritySettingsProps = {
  securityEmail: string;
};

type FormFields = {
  securityEmail: string;
};

const SecuritySettings = ({ securityEmail }: SecuritySettingsProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  useTitle(`Organization Settings · Security Settings · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSecurityEmail,
  });

  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<FormFields>({
    defaultValues: { securityEmail: securityEmail || "" },
    mode: "onChange",
  });

  useEffect(() => {
    reset({ securityEmail: securityEmail || "" });
  }, [reset, securityEmail]);

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const [updateSecurityEmail, { loading }] = useMutation(UPDATE_SECURITY_EMAIL, {
    refetchQueries: ["GetAccountSettings"],
  });

  const onSubmit = async ({ securityEmail: email }: FormFields) => {
    try {
      trackSegmentAnalyticsEvent("Save");
      await updateSecurityEmail({ variables: { securityEmail: email } });
      reportSuccess({ message: "Security Email Contact Updated" });
    } catch (e) {
      onError(e);
    }
  };

  return (
    <>
      <PageInfoHeader title="Security email" />
      <PageWrapper>
        <Box direction="column" limitWidth="medium" gap="x-large" fullWidth>
          <Card>
            <Box direction="column" fullWidth>
              <FormField
                label="Email"
                helperText="We will use this email to contact you in case of any security incidents."
              >
                {({ ariaInputProps }) => (
                  <Input
                    type="email"
                    placeholder="security@example.com"
                    {...register("securityEmail")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
              <Box justify="end" gap="large" margin="x-large 0 0">
                {isDirty && (
                  <Button variant="secondary" onPress={() => reset()} disabled={loading}>
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  onPress={() => handleSubmit(onSubmit)()}
                  disabled={!isDirty || loading}
                  loading={loading}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      </PageWrapper>
    </>
  );
};

export default SecuritySettings;
